// src/components/Apps/AppView.jsx
import React, { useState, useEffect } from 'react';
import { appsApi, orgsApi, projectsApi } from '../../services/api';
import { AppWindow, Plus, Edit2, Trash2, X, Tag, Cpu } from 'lucide-react';

const TYPE_STYLES = {
  web:            { color: '#60a5fa', bg: 'rgba(59,130,246,.15)',  icon: '🌐', label: 'Web' },
  api:            { color: '#a78bfa', bg: 'rgba(139,92,246,.15)', icon: '⚡', label: 'API' },
  worker:         { color: '#fbbf24', bg: 'rgba(245,158,11,.15)', icon: '⚙️', label: 'Worker' },
  mobile:         { color: '#34d399', bg: 'rgba(16,185,129,.15)', icon: '📱', label: 'Mobile' },
  service:        { color: '#fb923c', bg: 'rgba(249,115,22,.15)', icon: '🔧', label: 'Service' },
  database:       { color: '#4ade80', bg: 'rgba(74,222,128,.15)', icon: '🗄️', label: 'Database' },
  infrastructure: { color: '#f87171', bg: 'rgba(239,68,68,.15)', icon: '🏗️', label: 'Infrastructure' },
};
const STATUS_STYLES = {
  active:     { color: '#22c55e', label: 'Active' },
  inactive:   { color: '#64748b', label: 'Inactive' },
  deprecated: { color: '#ef4444', label: 'Deprecated' },
};
const SOURCE_COLORS = { GCP: '#4285f4', AZURE: '#0078d4', MONGODB: '#4ade80', DATABASE: '#a78bfa' };
const ALL_SOURCES   = ['GCP', 'AZURE', 'MONGODB', 'DATABASE'];

const EMPTY_FORM = { name: '', description: '', orgId: '', projectId: '', type: 'api', language: '', framework: '', version: '', sources: [], servicePattern: '', tags: '', status: 'active' };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-2xl rounded-xl p-6 overflow-y-auto max-h-[90vh]"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 25px 50px rgba(0,0,0,.5)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} style={{ color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' };

function Field({ label, children, half }) {
  return (
    <div className={half ? '' : 'mb-4'}>
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      {children}
    </div>
  );
}

export default function AppView() {
  const [apps, setApps]           = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [deleteId, setDeleteId]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [appList, orgList, projList] = await Promise.all([appsApi.list(), orgsApi.list(), projectsApi.list()]);
      setApps(appList);
      setOrgs(orgList);
      setProjects(projList);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openCreate() { setForm(EMPTY_FORM); setModal('create'); }
  function openEdit(app) {
    setForm({ name: app.name, description: app.description || '', orgId: String(app.orgId || ''), projectId: String(app.projectId || ''), type: app.type || 'api', language: app.language || '', framework: app.framework || '', version: app.version || '', sources: app.sources || [], servicePattern: app.servicePattern || '', tags: (app.tags || []).join(', '), status: app.status || 'active' });
    setModal(app);
  }

  function toggleSource(src) {
    setForm(f => ({ ...f, sources: f.sources.includes(src) ? f.sources.filter(s => s !== src) : [...f.sources, src] }));
  }

  async function save() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      if (modal === 'create') await appsApi.create(payload);
      else await appsApi.update(modal._id, payload);
      setModal(null);
      load();
    } finally { setSaving(false); }
  }

  async function confirmDelete(id) {
    await appsApi.delete(id);
    setDeleteId(null);
    load();
  }

  const filteredProjects = form.orgId ? projects.filter(p => String(p.orgId) === form.orgId) : projects;
  const displayed = apps.filter(a => {
    if (orgFilter !== 'ALL' && String(a.orgId) !== orgFilter) return false;
    if (typeFilter !== 'ALL' && a.type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <AppWindow size={20} style={{ color: '#60a5fa' }} />
        <div>
          <h1 className="text-base font-semibold">Applications</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Manage applications, their sources, and log routing patterns</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
            <option value="ALL">All Organizations</option>
            {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
            <option value="ALL">All Types</option>
            {Object.entries(TYPE_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
            <Plus size={14} /> New Application
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {displayed.map(app => {
              const org  = orgs.find(o => String(o._id) === String(app.orgId));
              const proj = projects.find(p => String(p._id) === String(app.projectId));
              return <AppCard key={app._id} app={app} orgName={org?.name} projName={proj?.name} onEdit={() => openEdit(app)} onDelete={() => setDeleteId(app._id)} />;
            })}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'create' ? 'New Application' : 'Edit Application'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-x-4">
            <div className="col-span-2 mb-4">
              <Field label="Application Name *">
                <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Payment API" />
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Organization">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.orgId} onChange={e => setForm(f => ({ ...f, orgId: e.target.value, projectId: '' }))}>
                  <option value="">Select org…</option>
                  {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Project">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.projectId} onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))}>
                  <option value="">Select project…</option>
                  {filteredProjects.map(p => <option key={p._id} value={String(p._id)}>{p.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Type">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {Object.entries(TYPE_STYLES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Status">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Language">
                <input style={inputStyle} value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} placeholder="e.g. Node.js, Python, Go" />
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Framework">
                <input style={inputStyle} value={form.framework} onChange={e => setForm(f => ({ ...f, framework: e.target.value }))} placeholder="e.g. Express, FastAPI" />
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Version">
                <input style={inputStyle} value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="e.g. 1.4.2" />
              </Field>
            </div>
            <div className="mb-4">
              <Field label="Service Pattern (log matcher)">
                <input style={inputStyle} value={form.servicePattern} onChange={e => setForm(f => ({ ...f, servicePattern: e.target.value }))} placeholder="e.g. payment-api" />
              </Field>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>Log Sources</label>
            <div className="flex gap-2">
              {ALL_SOURCES.map(src => (
                <button key={src} onClick={() => toggleSource(src)}
                  className="px-3 py-1 rounded text-xs font-semibold"
                  style={{
                    background: form.sources.includes(src) ? `${SOURCE_COLORS[src]}25` : 'var(--bg-tertiary)',
                    color: form.sources.includes(src) ? SOURCE_COLORS[src] : 'var(--text-3)',
                    border: `1px solid ${form.sources.includes(src) ? SOURCE_COLORS[src] + '60' : 'var(--border)'}`,
                    cursor: 'pointer',
                  }}>
                  {src}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-2 mb-4">
            <Field label="Description">
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What does this application do?" />
            </Field>
          </div>
          <div className="col-span-2 mb-4">
            <Field label="Tags (comma-separated)">
              <input style={inputStyle} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. critical, payment, external" />
            </Field>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setModal(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={save} disabled={saving || !form.name.trim()}
              style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(59,130,246,.2)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? .5 : 1 }}>
              {saving ? 'Saving…' : (modal === 'create' ? 'Create' : 'Save')}
            </button>
          </div>
        </Modal>
      )}

      {deleteId && (
        <Modal title="Delete Application?" onClose={() => setDeleteId(null)}>
          <p className="text-sm mb-5" style={{ color: 'var(--text-2)' }}>This will permanently delete the application and its configuration.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteId(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => confirmDelete(deleteId)} style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(239,68,68,.2)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AppCard({ app, orgName, projName, onEdit, onDelete }) {
  const type   = TYPE_STYLES[app.type]     || TYPE_STYLES.api;
  const status = STATUS_STYLES[app.status] || STATUS_STYLES.active;
  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span style={{ fontSize: 20 }}>{type.icon}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{app.name}</span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: type.bg, color: type.color }}>{type.label}</span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: `${status.color}18`, color: status.color }}>{status.label}</span>
            </div>
            <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-3)' }}>
              {[orgName, projName].filter(Boolean).join(' / ') || 'Unassigned'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          <IconBtn icon={Edit2} onClick={onEdit} />
          <IconBtn icon={Trash2} onClick={onDelete} danger />
        </div>
      </div>

      {app.description && (
        <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-2)' }}>{app.description}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-2">
        {(app.language || app.framework) && (
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
            <Cpu size={10} /> {[app.language, app.framework].filter(Boolean).join(' · ')}
            {app.version && ` v${app.version}`}
          </span>
        )}
        {(app.sources || []).map(src => (
          <span key={src} className="text-xs px-2 py-0.5 rounded font-semibold"
            style={{ background: `${SOURCE_COLORS[src]}18`, color: SOURCE_COLORS[src], border: `1px solid ${SOURCE_COLORS[src]}30` }}>
            {src}
          </span>
        ))}
      </div>

      {(app.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {app.tags.slice(0, 4).map(t => (
            <span key={t} className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
              <Tag size={9} /> {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger }) {
  return (
    <button onClick={onClick} style={{ padding: 5, borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: danger ? '#f87171' : 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}>
      <Icon size={13} />
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <AppWindow size={40} style={{ color: 'var(--text-3)' }} />
      <div className="text-sm font-medium">No applications yet</div>
      <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-3)' }}>Register your applications to track logs, errors, and performance by service.</p>
      <button onClick={onAdd}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
        style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
        <Plus size={14} /> New Application
      </button>
    </div>
  );
}
